import Constants from "../config/Constants";
import ApiEndpoint from "../config/ApiEndpoint";
import { compressImageForUpload } from "./compressImageForUpload";

export function resolveAuthToken(profile) {
  const source = profile || {};
  const token = source.token || source.data?.token;
  return typeof token === "string" && token.trim() ? token.trim() : null;
}

/**
 * Upload an image to user/upload with auth, compression, and consistent error handling.
 */
export async function uploadImageFile(file, token, { compress = true } = {}) {
  if (!file) {
    return { status: false, message: "No file selected." };
  }

  const authToken = typeof token === "string" ? token.trim() : null;
  if (!authToken) {
    return { status: false, message: "Session expired. Please log in again." };
  }

  let fileToUpload = file;
  if (compress) {
    try {
      fileToUpload = await compressImageForUpload(file);
    } catch (error) {
      return {
        status: false,
        message: error?.message || "Could not process image. Please use a smaller file.",
      };
    }
  }

  const formData = new FormData();
  formData.append("file", fileToUpload, fileToUpload.name);
  formData.append("type", "image");

  try {
    const response = await fetch(`${Constants.BASE_API_URL}user/upload`, {
      method: "POST",
      headers: { "x-access-token": authToken },
      body: formData,
    });

    if (response.status === 413) {
      return {
        status: false,
        message: "Image is too large for the server. Please use a file under 5 MB.",
      };
    }

    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    if (response.status === 401) {
      return { status: false, message: data?.message || "Session expired. Please log in again." };
    }

    if (response.status === 403) {
      return {
        status: false,
        message: data?.message || "Authentication failed. Please log in again.",
      };
    }

    if (!response.ok) {
      return {
        status: false,
        message: data?.message || `Upload failed (${response.status})`,
      };
    }

    return data;
  } catch (error) {
    console.error("Upload error", error);
    return { status: false, message: "Upload failed. Please try again." };
  }
}

/**
 * Public logo upload during master org registration (no JWT — invite code authorizes).
 */
export async function uploadRegistrationLogoFile(file, inviteCode, { compress = true } = {}) {
  if (!file) {
    return { status: false, message: "No file selected." };
  }

  const code = typeof inviteCode === "string" ? inviteCode.trim() : "";
  if (!code) {
    return { status: false, message: "Enter your club invite code before uploading a logo." };
  }

  let fileToUpload = file;
  if (compress) {
    try {
      fileToUpload = await compressImageForUpload(file);
    } catch (error) {
      return {
        status: false,
        message: error?.message || "Could not process image. Please use a smaller file.",
      };
    }
  }

  const formData = new FormData();
  formData.append("code", code);
  formData.append("file", fileToUpload, fileToUpload.name);

  try {
    const response = await fetch(ApiEndpoint.MASTER_ORG_UPLOAD, {
      method: "POST",
      body: formData,
    });

    if (response.status === 413) {
      return {
        status: false,
        message: "Image is too large for the server. Please use a file under 5 MB.",
      };
    }

    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    if (!response.ok) {
      return {
        status: false,
        message: data?.message || `Upload failed (${response.status})`,
      };
    }

    return data;
  } catch (error) {
    console.error("Registration logo upload error", error);
    return { status: false, message: "Upload failed. Please try again." };
  }
}
